'use client'

import {type ReactNode, useEffect, useRef, useState} from 'react'
import {menuItemsFor, filterMenuItemsForRole, type MenuKey, menus} from '@/lib/constants'
import {getAuthUser} from '@/lib/api'

type SecondaryColumnProps = {
    activeMenu: MenuKey
    activeItemId: string
    isLoggedIn: boolean
    onNavigate: (path: string) => void
    onLogout?: () => void
    children?: ReactNode
}

const MIN_WIDTH = 240
const MAX_WIDTH = 420
const DEFAULT_WIDTH = 260
const WIDTH_STORAGE_KEY = 'wechat-web-secondary-column-width'

export function SecondaryColumn({activeMenu, activeItemId, isLoggedIn, onNavigate, onLogout, children}: SecondaryColumnProps) {
    const menu = menus[activeMenu]
    const userRole = getAuthUser()?.role || ''
    const items = filterMenuItemsForRole(menuItemsFor(activeMenu, isLoggedIn), userRole)
    const isConversation = activeMenu === 'conversation'
    const singleConversationItem = isConversation && items.length === 1
    const showSubNav = !singleConversationItem

    const [width, setWidth] = useState(DEFAULT_WIDTH)
    const [compactViewport, setCompactViewport] = useState(false)
    const [dragging, setDragging] = useState(false)
    const widthRef = useRef(DEFAULT_WIDTH)

    useEffect(() => {
        const media = window.matchMedia('(max-width: 1535px)')
        const update = () => setCompactViewport(media.matches)
        update()
        media.addEventListener('change', update)
        return () => media.removeEventListener('change', update)
    }, [])

    useEffect(() => {
        const raw = window.localStorage.getItem(WIDTH_STORAGE_KEY)
        if (!raw) return
        const value = Number(raw)
        if (Number.isFinite(value) && value >= MIN_WIDTH && value <= 520) {
            const next = Math.min(value, compactViewport ? DEFAULT_WIDTH : MAX_WIDTH)
            setWidth(next)
            widthRef.current = next
        }
    }, [compactViewport])

    useEffect(() => {
        if (!dragging) return
        const onMove = (event: MouseEvent) => {
            const next = Math.min(Math.max(event.clientX - 60, MIN_WIDTH), MAX_WIDTH)
            widthRef.current = next
            setWidth(next)
        }
        const onUp = () => {
            setDragging(false)
            window.localStorage.setItem(WIDTH_STORAGE_KEY, String(widthRef.current))
        }
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        document.addEventListener('mousemove', onMove)
        document.addEventListener('mouseup', onUp)
        return () => {
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            document.removeEventListener('mousemove', onMove)
            document.removeEventListener('mouseup', onUp)
        }
    }, [dragging])

    return (
        <aside
            className="relative hidden md:flex h-full bg-sub border-r border-hair flex-col shrink-0"
            style={{width}}
        >
            {showSubNav ? (
                <>
                    <div className="h-[60px] flex items-center px-4 border-b border-hair/60 shrink-0">
                        <h1 className="text-sm font-medium text-ink">{menu.label}</h1>
                    </div>
                    <nav className="flex-1 overflow-y-auto py-2">
                        {items.map((item, index) => {
                            const Icon = item.icon
                            const active = activeItemId === item.id
                            const group = (item as { group?: string }).group
                            const prevGroup = index > 0 ? (items[index - 1] as { group?: string }).group : undefined
                            const showGroupHeader = !!group && group !== prevGroup
                            return (
                                <div key={item.id}>
                                    {showGroupHeader ? (
                                        <div className="px-4 pt-3 pb-1 text-[11px] text-muted uppercase tracking-wider">
                                            {group}
                                        </div>
                                    ) : null}
                                    <button
                                        type="button"
                                        className={`w-full flex items-center gap-3 h-11 px-4 transition-colors text-sm ${
                                            active
                                                ? 'bg-subActive text-ink font-medium'
                                                : 'text-soft hover:bg-subHover'
                                        }`}
                                        onClick={() => {
                                            if (item.id === 'logout') {
                                                onLogout?.()
                                                return
                                            }
                                            onNavigate(item.path)
                                        }}
                                    >
                                        <Icon size={18}/>
                                        <span>{item.label}</span>
                                    </button>
                                </div>
                            )
                        })}
                    </nav>
                </>
            ) : null}
            {isConversation ? (
                <div className="flex-1 min-h-0 flex flex-col">{children}</div>
            ) : null}
            <div
                role="separator"
                aria-orientation="vertical"
                className={`absolute top-0 right-0 w-1 h-full cursor-col-resize z-20 ${
                    dragging ? 'bg-accent/40' : 'hover:bg-accent/30'
                }`}
                onMouseDown={(event) => {
                    event.preventDefault()
                    setDragging(true)
                }}
                onDoubleClick={() => {
                    widthRef.current = DEFAULT_WIDTH
                    setWidth(DEFAULT_WIDTH)
                    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(DEFAULT_WIDTH))
                }}
                title="拖动调整宽度（双击恢复默认）"
            />
        </aside>
    )
}
